import { styled, ThemeProvider } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { CrossIcon } from "common/components/icons";
import { UIButton } from "common/components/index";
import { breakpoints } from "common/styles";
import { useLocalStorageState } from "common/utilities/localStorage";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { useWeforumUrl } from "common/utilities/useWeforumUrl";
import type { TrackEvent, TrackPage } from "domains/analytics/types";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { darkTheme, lightTheme } from "domains/theme/constants/theme";
import { useTheme } from "domains/theme/useTheme";
import { getShouldShowBecomeMemberMarketing } from "entities/permissions/selectors";
import { useGoToMembership } from "entities/user/utils";

const StyledContainer = styled.div`
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundPrimary};
  color: ${({ theme }) => theme.colors.textBright};
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 50px;
  max-height: 600px;
  transition:
    min-height 0.3s,
    max-height 0.3s;
  border-radius: 5px;
`;

const StyledMessageContainer = styled.div`
  margin-right: 20px;
  margin-bottom: 20px;
`;

const StyledButtonContainer = styled.div<{ hasDismissAction?: boolean }>`
  display: flex;
  align-items: center;
  margin: 0;
  width: 100%;

  @media ${breakpoints.medium} {
    width: auto;
    padding-right: ${({ hasDismissAction }) => (hasDismissAction ? 2 : 0)}rem;
  }
`;

const StyledCloseIcon = styled(CrossIcon)`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 25px;
  height: 25px;
  color: ${({ theme }) => theme.colors.textFade};
  cursor: pointer;
  margin-left: 3rem;
  transition: all 0.3s;
  border-radius: 20px;
  padding: 7px;
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
    background: ${({ theme }) => theme.colors.textBright}0F;
  }
`;

const LoginButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const StyledLabel = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.textFade};
`;

const StyledTitle = styled.h3`
  font-size: 1.4rem;
  margin: 0;
`;

const StyledMessage = styled.p`
  margin: 1rem 0 0;
  font-size: 1rem;
  line-height: 1.4rem;
`;

type JoinUsUpgradeBannerProps = { className?: string; trackPage: TrackPage };

export const JoinUsUpgradeBanner = ({ className, trackPage }: JoinUsUpgradeBannerProps) => {
  const dispatch = useDispatch();
  const goToMembership = useGoToMembership();
  const [dismissedModalValue, setDismissedModalValue] = useLocalStorageState("topic_detail_call_to_action_banner");
  const isAuthenticated = useSelector(getIsAuthenticated);
  const showUpgradeBanner = useSelector(getShouldShowBecomeMemberMarketing);
  const theme = useTheme();
  const track = useTrack();
  const translate = useTranslate();
  const weforumUrl = useWeforumUrl();

  const handleUpgradeLinkClick = () => {
    track({ name: "Upgrade", page: trackPage, subpage: "Message banner" });
    goToMembership();
  };

  const onLoginRegisterClick = (name: TrackEvent) => () => {
    track({ name, eventType: "initialize modal" });
    dispatch(triggerLogin());
  };

  const onDismissClick = () => {
    const date = new Date();
    setDismissedModalValue(date.getTime());
  };

  if (dismissedModalValue || (isAuthenticated && !showUpgradeBanner)) return null;

  const upgradeButton = (
    <UIButton data-test-id="topic-upgrade-banner-upgrade-button" onClick={handleUpgradeLinkClick} variant="outlined">
      {translate("app.message_banner.upgrade.button")}
    </UIButton>
  );

  const loginSignupButtons = (
    <LoginButtonsContainer>
      <UIButton
        as="a"
        data-test-id="topic-upgrade-banner-signup-button"
        href={`${weforumUrl}/join-us`}
        variant="filled"
      >
        {translate("app.registration.join_us")}
      </UIButton>
      <UIButton
        data-test-id="topic-upgrade-banner-login-button"
        onClick={onLoginRegisterClick("Login")}
        variant="outlined"
      >
        {translate("app.user.sign_in")}
      </UIButton>
    </LoginButtonsContainer>
  );

  const button = isAuthenticated ? upgradeButton : loginSignupButtons;

  const message = isAuthenticated ? (
    <>
      <StyledTitle>{translate("app.upgrade_banner.title")}</StyledTitle>
      <StyledMessage>{translate("app.upgrade_banner.message")}</StyledMessage>
    </>
  ) : (
    <>
      <StyledLabel>{translate("app.discover.topic.tabs.sign_up_ad.label")}</StyledLabel>
      <StyledTitle>{translate("app.discover.topic.tabs.sign_up_ad.title")}</StyledTitle>
      <StyledMessage>{translate("app.discover.topic.tabs.sign_up_ad.expert_analysis")}</StyledMessage>
      <StyledMessage>{translate("app.discover.topic.tabs.sign_up_ad.research")}</StyledMessage>
    </>
  );

  return (
    <ThemeProvider theme={theme === "light" ? darkTheme : lightTheme}>
      <StyledContainer className={className}>
        <StyledMessageContainer>{message}</StyledMessageContainer>
        <StyledButtonContainer hasDismissAction={!!onDismissClick}>{button}</StyledButtonContainer>
        {onDismissClick && <StyledCloseIcon onClick={onDismissClick} />}
      </StyledContainer>
    </ThemeProvider>
  );
};
